import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslocoModule } from '@jsverse/transloco';
import { Subject, timer } from 'rxjs';
import { switchMap, takeUntil, takeWhile } from 'rxjs/operators';
import { ImageStudioService } from '../../../Services/image-studio.service';
import {
  EditRecipe, GenerateOperation, GenerateOptions, ImageAsset, ImageQuota, QuotaBlock, RenderJob,
  SCENE_DETAIL_MAX, SCENE_SETTINGS, SceneSetting,
  isDefaultFraming, isMarketingAsset, isNeutralRecipe, neutralRecipe, pocQuotaFor, quotaBlock,
} from './image-studio.model';

/** The screens, in the order an artist meets them. */
export type StudioScreen = 'open' | 'adjust' | 'scene' | 'working' | 'review' | 'versions' | 'tools';

/**
 * How long to keep polling a job before calling it stalled. Generous, because the
 * provider's own timeout is four minutes and a big render is genuinely slow — but
 * finite, because a stalled worker otherwise spins the progress bar for ever.
 */
export const STUDIO_JOB_TIMEOUT_MS = 5 * 60 * 1000;

/** One adjustment slider. `key` indexes RecipeAdjustments; sharpen is handled separately. */
interface SliderDef {
  key: 'Zoom' | 'Rotate' | 'Brightness' | 'Contrast' | 'Temperature' | 'Saturation' | 'Vibrance' | 'SharpenAmount';
  labelKey: string;
  min: number;
  max: number;
  step: number;
  /** Suffix shown beside the value, e.g. the degree sign on Straighten. */
  unit: string;
}

@Component({
  selector: 'app-image-studio',
  imports: [CommonModule, FormsModule, TranslocoModule],
  templateUrl: './image-studio.component.html',
  styleUrls: ['./image-studio.component.scss'],
})
export class ImageStudioComponent implements OnDestroy {

  /** The artwork's current image — becomes the mock `Original` until uploads are wired. */
  @Input() imageUrl = '';
  @Input() artistProductId = '';
  @Input() productName = '';

  @Input()
  set visible(value: boolean) {
    const opening = value && !this._visible;
    this._visible = value;
    if (opening) this.open();
  }
  get visible(): boolean { return this._visible; }
  private _visible = false;

  @Output() visibleChange = new EventEmitter<boolean>();
  /** Emitted when an image is accepted — the host re-reads the artwork. */
  @Output() imageAccepted = new EventEmitter<ImageAsset>();
  @Output() closed = new EventEmitter<void>();

  /**
   * A tool the studio hands off to rather than implements. `perspective` opens the
   * existing editor at corner-picking, which is the FAITHFUL path for flat artwork:
   * a painting is rectified by measuring its corners, never by regenerating it.
   *
   * Deliberately not `closed` — the host still needs to know which photo was being
   * worked on, and `closed` resets that.
   */
  @Output() openTool = new EventEmitter<{ tool: 'perspective'; sourceAssetId: string | null }>();

  public screen: StudioScreen = 'open';
  public assets: ImageAsset[] = [];
  public quota: ImageQuota = pocQuotaFor('Free');
  public isBusy = false;
  /** Adopting the artwork’s existing photo as its Original, first time only. */
  public isPreparing = false;

  /** The asset for the photo the artist clicked. Everything is scoped to this. */
  public rootAssetId: string | null = null;
  public errorKey = '';
  /** The server's own words, shown under the friendly message when it has any. */
  public errorDetail = '';

  /** The asset the artist is working from — the corrected one if there is one. */
  public workingAsset: ImageAsset | null = null;
  /** A provisional generated result, awaiting accept or decline. */
  public pendingAsset: ImageAsset | null = null;
  /** Which outcome produced `pendingAsset`, so "Try again" repeats the right one. */
  public lastOperation: GenerateOperation = 'Catalogue';
  /** And with which setting — a retry must reproduce the same request, not the default one. */
  private lastOptions: GenerateOptions = {};

  public recipe: EditRecipe = neutralRecipe('');
  /** Before/after divider position, 0–100. */
  public comparePercent = 50;

  // ------------------------------------------------------------- product shot

  public readonly sceneSettings: SceneSetting[] = SCENE_SETTINGS;
  public readonly SCENE_DETAIL_MAX = SCENE_DETAIL_MAX;

  /**
   * The chosen setting. Pre-selected rather than left blank: every option produces a usable shot,
   * so an empty state here is a decision the artist has no way to get wrong and no reason to be
   * asked for. They change it if they want something else.
   */
  public sceneSettingId: string = SCENE_SETTINGS[0].id;

  /** The artist's own words about the SETTING. Applied to the surroundings only. */
  public sceneDetail = '';

  public readonly sliders: SliderDef[] = [
    // Framing sits with the other geometry, above the light and colour controls — the artist
    // decides what is in the picture before deciding how it looks.
    { key: 'Zoom',          labelKey: 'imageStudio.framing',     min: 0.25, max: 4,  step: 0.01, unit: '×' },
    { key: 'Rotate',        labelKey: 'imageStudio.straighten',  min: -6,  max: 6,   step: 0.1, unit: '°' },
    { key: 'Brightness',    labelKey: 'imageStudio.brightness',  min: -1,  max: 1,   step: 0.01, unit: '' },
    { key: 'Contrast',      labelKey: 'imageStudio.contrast',    min: -1,  max: 1,   step: 0.01, unit: '' },
    { key: 'Temperature',   labelKey: 'imageStudio.warmth',      min: -1,  max: 1,   step: 0.01, unit: '' },
    // Vibrance sits above Saturation deliberately: it is the one to reach for first on a photograph
    // of artwork, because it lifts the muted passages without touching pigment that is already at
    // full strength. Saturation moves every colour equally, which is stronger and blunter.
    { key: 'Vibrance',      labelKey: 'imageStudio.vibrance',    min: -1,  max: 1,   step: 0.01, unit: '' },
    { key: 'Saturation',    labelKey: 'imageStudio.saturation',  min: -1,  max: 1,   step: 0.01, unit: '' },
    { key: 'SharpenAmount', labelKey: 'imageStudio.sharpness',   min: 0,   max: 1,   step: 0.01, unit: '' },
  ];

  private destroy$ = new Subject<void>();
  private jobPoll$ = new Subject<void>();

  constructor(private studio: ImageStudioService) {}

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.jobPoll$.next();
    this.jobPoll$.complete();
  }

  // ------------------------------------------------------------- lifecycle

  private open(): void {
    this.screen = 'open';
    this.errorKey = '';
    this.errorDetail = '';
    this.pendingAsset = null;
    this.comparePercent = 50;
    this.rootAssetId = null;
    this.workingAsset = null;
    this.assets = [];
    // Closing the studio mid-job left isBusy true, and nothing reset it on the way back
    // in: canGenerate stayed false, both outcome buttons were disabled, and the screen
    // gave no reason. Reopening is a fresh start — anything in flight was abandoned.
    this.isBusy = false;
    this.isPreparing = false;
    // A setting chosen for the last artwork must not carry into this one — the studio instance is
    // reused across photos, so anything left here would be an instruction nobody gave.
    this.sceneSettingId = SCENE_SETTINGS[0].id;
    this.sceneDetail = '';
    this.lastOptions = {};
    // Framing, the name in waiting and the version being viewed belong to ONE photo. Left behind
    // they are the same fault as a scene setting that carries over: the artist opens a different
    // photograph and finds it already shrunk and pushed into a corner by an instruction they gave
    // about something else.
    this.zoom = 1;
    this.frameX = 0.5;
    this.frameY = 0.5;
    this.pendingLabel = '';
    this.selectedVersionId = null;
    this.naturalWidth = 0;
    this.naturalHeight = 0;

    this.studio.getQuota().pipe(takeUntil(this.destroy$))
      .subscribe(q => this.quota = q);

    this.loadAssets(true);
  }

  private loadAssets(bootstrapIfEmpty = false): void {
    this.studio.getAssets(this.artistProductId, this.imageUrl)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: list => {
          this.assets = list || [];

          // Anchor everything to the photo the artist actually clicked. Assets are
          // stored per ARTWORK, but the pencil is per PHOTO — so an artwork with
          // several images has several lineages, and picking "any Original" showed
          // people a different picture from the one they asked to edit.
          // Keep an anchor we already established (e.g. straight after upload); only
          // resolve from the URL when we don't have one yet.
          if (!this.rootAssetId || !this.assets.some(a => a.Id === this.rootAssetId)) {
            const root = this.assets.find(a => this.sameImage(a.Url, this.imageUrl));
            this.rootAssetId = root ? root.Id : null;
          }

          this.workingAsset = this.preferredWorkingAsset(this.lineage);
          this.recipe = neutralRecipe(this.rootAssetId || '');

          if (bootstrapIfEmpty && !this.rootAssetId) {
            this.bootstrapOriginal();
          }
        },
        error: () => this.fail('imageStudio.errorLoadFailed'),
      });
  }

  /**
   * URLs can differ by transformation suffix or cache-buster while pointing at the
   * same stored image, so compare on the filename rather than the whole string.
   */
  private sameImage(a: string, b: string): boolean {
    if (!a || !b) { return false; }
    if (a === b) { return true; }
    const leaf = (u: string) => (u.split('?')[0].split('/').pop() || '').toLowerCase();
    const la = leaf(a), lb = leaf(b);
    return !!la && la === lb;
  }

  /**
   * Artworks that predate the image pipeline have a ProductImgUrl but NO ImageAsset
   * rows, so there is nothing to generate from — the screen opens looking fine (the
   * template falls back to the artwork's URL) and then every action fails with no
   * source.
   *
   * Adopt the existing photo as the Original: fetch the bytes we are already
   * displaying and upload them. One-time per artwork, and it is what makes "your
   * original is safe" true for work that is already in the catalogue.
   */
  /**
   * Establishes which stored asset this studio session is working from.
   *
   * ASKS THE SERVER TO ADOPT, never re-creates. Until 2026-08-22 this fetched the artwork's photo
   * into the browser and uploaded it back to prove identity, which produced a new storage object
   * and a new `Original` row every time the URL failed to match — eleven for one bowl, each
   * starting a lineage disconnected from the artwork's cover, so nothing the artist did could
   * reach the artwork.
   *
   * The copy protected nothing: assets are append-only, and deletion is soft, artist-initiated and
   * refused on the current image. It only charged a full-size download and upload on every open,
   * which on a phone is the whole file twice before the artist has done anything.
   *
   * Adoption is idempotent and moves no bytes, so opening the studio repeatedly is free.
   */
  private bootstrapOriginal(): void {
    if (!this.imageUrl || !this.artistProductId) { return; }
    this.isBusy = true;
    this.isPreparing = true;

    this.studio.adoptExisting(this.artistProductId, this.imageUrl)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: adopted => {
          this.isBusy = false;
          this.isPreparing = false;
          // Anchor from the RESPONSE, not by re-matching URLs: the stored asset may be served
          // from different storage or renamed, and a failed match would leave the studio with
          // no root and every action dead. The id is the identity; the filename never was.
          if (adopted && adopted.Id) { this.rootAssetId = adopted.Id; }
          this.loadAssets(false);
        },
        error: () => {
          this.isBusy = false;
          this.isPreparing = false;
          this.fail('imageStudio.errorPrepareFailed');
        },
      });
  }

  public close(): void {
    this.jobPoll$.next();
    this._visible = false;
    this.visibleChange.emit(false);
    this.closed.emit();
  }

  /**
   * Navigate. Clears any stale error — EXCEPT when the caller is reporting one, or the
   * message is wiped by the very navigation that follows it and every failure becomes
   * silent. That is exactly what "I click and nothing happens" was.
   */
  public go(screen: StudioScreen, keepError = false): void {
    if (!keepError) { this.errorKey = ''; this.errorDetail = ''; }
    this.screen = screen;
  }

  // ------------------------------------------------------------- selectors

  /**
   * Everything derived from the photo the artist clicked — that asset, plus anything
   * whose SourceAssetId chain leads back to it. This is what keeps one artwork's
   * several photographs from bleeding into each other.
   */
  public get lineage(): ImageAsset[] {
    if (!this.rootAssetId) { return []; }
    const byId = new Map(this.assets.map(a => [a.Id, a]));
    return this.assets.filter(a => {
      let cur: ImageAsset | undefined = a;
      const seen = new Set<string>();
      while (cur && !seen.has(cur.Id)) {
        if (cur.Id === this.rootAssetId) { return true; }
        seen.add(cur.Id);
        cur = cur.SourceAssetId ? byId.get(cur.SourceAssetId) : undefined;
      }
      return false;
    });
  }

  /**
   * What to open on: the artist's own photograph, or the correction they last made TO IT.
   *
   * Scoped to the clicked photo's lineage, which it was not before. Taking the first `Corrected`
   * asset on the artwork meant opening any photograph showed a correction made to a different one
   * — an artist clicking their untouched cover was shown a shrunken, repositioned version of
   * something else and reasonably concluded the studio had mangled their picture.
   *
   * Within the lineage, what the artwork is USING wins, then the most recent correction, then the
   * original. A correction still beats the raw original as a starting point — it is simply the
   * right photo's correction now.
   */
  private preferredWorkingAsset(list: ImageAsset[]): ImageAsset | null {
    const byId = new Map(list.map(a => [a.Id, a]));

    const descendsFromRoot = (asset: ImageAsset): boolean => {
      let cur: ImageAsset | undefined = asset;
      const seen = new Set<string>();
      while (cur && !seen.has(cur.Id)) {
        if (cur.Id === this.rootAssetId) { return true; }
        seen.add(cur.Id);
        cur = cur.SourceAssetId ? byId.get(cur.SourceAssetId) : undefined;
      }
      return false;
    };

    const mine = this.rootAssetId ? list.filter(descendsFromRoot) : list;

    const newestFirst = (a: ImageAsset, b: ImageAsset) =>
      (b.CreatedDate || '').localeCompare(a.CreatedDate || '');

    return mine.find(a => a.IsCurrent && a.Kind !== 'Derivative')
        || mine.filter(a => a.Kind === 'Corrected').sort(newestFirst)[0]
        || mine.find(a => a.Kind === 'Original')
        || list.find(a => a.Kind === 'Original')
        || null;
  }

  /** The clicked photo itself. */
  public get original(): ImageAsset | undefined {
    return this.assets.find(a => a.Id === this.rootAssetId);
  }

  /**
   * What this photo currently resolves to.
   *
   * Read from `IsCurrent`, never from `Status`: the server marks originals and every
   * deterministic correction `Accepted`, so "first Accepted asset" picked whatever
   * happened to sort first — which after a render could be the 400px thumbnail.
   */
  public get acceptedAsset(): ImageAsset | undefined {
    return this.lineage.find(a => a.IsCurrent) || this.original;
  }

  /**
   * Declined results stay stored but never clutter the versions list — and neither do
   * derivatives. One render returns four assets (the full-size master plus catalogue,
   * web and thumb resizes of it); the artist made ONE edit and must see one entry.
   */
  public get visibleVersions(): ImageAsset[] {
    return this.lineage.filter(a => a.Status !== 'Declined' && a.Kind !== 'Derivative');
  }

  /** Blocked by the day, the month, or not at all. Own-key users are never blocked. */
  public get blockedBy(): QuotaBlock {
    return quotaBlock(this.quota);
  }

  public get canGenerate(): boolean {
    return this.blockedBy === 'none' && !this.isBusy;
  }

  /** Own-key artists supply their own credentials, so we show no counters at all. */
  public get isMetered(): boolean {
    return this.quota.Source === 'Plan';
  }

  /**
   * The message must name the RIGHT reset. Telling someone their allowance returns
   * tomorrow when they are capped for the month is simply untrue.
   */
  public get quotaMessageKey(): string {
    if (this.blockedBy === 'monthly') return 'imageStudio.errorNoneLeftMonth';
    if (this.blockedBy === 'daily') return 'imageStudio.errorNoneLeftToday';
    return '';
  }

  public get hasAdjustments(): boolean {
    return !isNeutralRecipe(this.recipe);
  }

  /**
   * A CSS filter approximating the recipe, for the live preview ONLY. The server's
   * render from the original is the authoritative result — this is deliberately just
   * close enough to decide by, never what gets saved.
   *
   * Vibrance is the one control this cannot represent honestly: the server weights the lift
   * by how much colour each pixel already has, and a CSS filter has no way to vary per pixel.
   * It is approximated as a gentler flat `saturate()`, which is roughly what the server does
   * to an averagely-muted photograph — but on a picture of vivid pigment the preview will show
   * more movement than the render delivers. Deciding between the two sliders is a job for the
   * rendered version, which is why the studio keeps them.
   */
  public get previewFilter(): string {
    const a = this.recipe.Adjustments;
    const brightness = 1 + a.Brightness * 0.5;
    const contrast = 1 + a.Contrast * 0.5;
    const saturate = (1 + a.Temperature * 0.15) * (1 + a.Saturation * 0.5) * (1 + a.Vibrance * 0.35);
    return `brightness(${brightness.toFixed(3)}) contrast(${contrast.toFixed(3)}) saturate(${saturate.toFixed(3)})`;
  }

  /**
   * Whether the stage is showing the artist's composition rather than a stored image.
   *
   * Includes `working`, and that is the fix for something that read as a bug: the artist framed
   * their piece, pressed Apply, and the stage snapped back to the full-size original — which looks
   * exactly like the edit being thrown away. The image underneath IS the untouched original (the
   * caption says so, and that reassurance is worth keeping), but there is no reason to drop the
   * composition off it while the server renders that very composition.
   *
   * It ends by itself: the recipe is reset to neutral the moment the job lands, so the transform
   * evaporates as the real result arrives.
   */
  public get showsPreview(): boolean {
    return this.screen === 'adjust' || this.screen === 'working';
  }

  public get previewTransform(): string {
    const rotate = `rotate(${this.recipe.Geometry.Rotate}deg)`;
    if (!this.isReframed) return rotate;

    // Percentages resolve against the IMG's border box, which is the whole square cell — but
    // `object-fit: contain` means the picture only occupies part of that square. Scaling the
    // percentage by the contained fraction converts "fraction of the artwork" into "fraction of
    // the box", which is what CSS will actually apply. Getting this wrong is invisible on a
    // square photograph and wrong on every other shape.
    //
    // The two modes place the work by DIFFERENT arithmetic, and this has to match the server in
    // each — they agree only at dead centre, which is exactly why zooming in looked right while
    // dragging a shrunken work did not.
    //
    //   crop  (zoom > 1): frameX is the CENTRE of the kept region, in source fractions.
    //   frame (zoom < 1): the server computes `x = (canvas − work) × X`, so X is a PLACEMENT —
    //                     0 is hard left, 1 is hard right — and the work's centre therefore lands
    //                     at `(1 − s)·X + s/2`. Dividing the wanted shift by the scale undoes the
    //                     magnification `scale()` applies to a translate that runs before it.
    let x: number;
    let y: number;

    if (this.usesFrame) {
      // The server centres the work then displaces it by a fraction of the CANVAS, so its centre
      // lands at `0.5 + offset`. Dividing by the scale undoes the magnification that `scale()`
      // applies to a translate running before it.
      x = (this.frameX - 0.5) / this.zoom * this.containedFractionX * 100;
      y = (this.frameY - 0.5) / this.zoom * this.containedFractionY * 100;
    } else {
      x = (0.5 - this.frameX) * this.containedFractionX * 100;
      y = (0.5 - this.frameY) * this.containedFractionY * 100;
    }

    // Right to left: translate, then scale, then rotate — the order the server renders in
    // (crop, then rotate), so the preview cannot disagree with the result about what happened.
    return `${rotate} scale(${this.zoom.toFixed(4)}) translate(${x.toFixed(4)}%, ${y.toFixed(4)}%)`;
  }

  // -------------------------------------------------- resize & reposition (POC)

  /**
   * Framing is held as zoom + centre rather than as the four numbers the recipe carries, because
   * that is the shape the interaction actually has: dragging moves a centre, a handle changes a
   * scale. `Crop` is derived from it on every change (`syncCrop`).
   *
   * Zoom and centre also make the invariant free. A single zoom applied to both axes keeps the
   * crop's proportions identical to the source's, so the work can never come back stretched — the
   * artist is choosing how much of their photograph to keep, never what shape it is.
   */
  public zoom = 1;
  public frameX = 0.5;
  public frameY = 0.5;

  /** Past about 4× a phone photograph is mush; the cap is a kindness, not a limitation. */
  public readonly maxZoom = 4;

  /** Below a quarter the work is a stamp in a field of white. Matches the server's floor. */
  public readonly minZoom = 0.25;

  /** The catalogue derivative's longest edge. Below it, a catalogue image starts from less. */
  private readonly catalogueEdge = 2000;

  /** Read off the loaded file, so the maths is against the real picture rather than an assumption. */
  private naturalWidth = 0;
  private naturalHeight = 0;

  private drag: {
    mode: 'move' | 'scale';
    pointerId: number;
    startX: number;
    startY: number;
    startFrameX: number;
    startFrameY: number;
    startZoom: number;
    startDistance: number;
    displayW: number;
    displayH: number;
  } | null = null;

  public onPreviewImageLoad(event: Event): void {
    const img = event.target as HTMLImageElement;
    this.naturalWidth = img.naturalWidth || 0;
    this.naturalHeight = img.naturalHeight || 0;
  }

  /** How much of the square cell the picture actually covers, per axis (`object-fit: contain`). */
  private get containedFractionX(): number {
    if (!this.naturalWidth || !this.naturalHeight) return 1;
    return Math.min(1, this.naturalWidth / this.naturalHeight);
  }

  private get containedFractionY(): number {
    if (!this.naturalWidth || !this.naturalHeight) return 1;
    return Math.min(1, this.naturalHeight / this.naturalWidth);
  }

  public get isReframed(): boolean {
    return !isDefaultFraming(this.recipe);
  }

  /**
   * True when the work is being placed on a canvas rather than zoomed into.
   *
   * Includes exactly 1×, and that is the point: a full-size work can still be MOVED, with ground
   * appearing behind it. Treating 1× as a crop is what made dragging do nothing until the artist
   * had first shrunk the photo — the crop of a whole image has no freedom in it.
   *
   * The two modes place the work by different arithmetic, so anything converting between the
   * artist's pointer and the recipe has to know which it is in. Read from the zoom rather than the
   * recipe, so it is settled before `syncCrop` has written anything.
   */
  public get usesFrame(): boolean {
    return this.zoom <= 1.0001;
  }

  /** Zoom as the artist reads it: 100% is the whole photograph. */
  public get zoomPercent(): number {
    return Math.round(this.zoom * 100);
  }

  public setZoom(raw: string | number): void {
    this.zoom = Math.min(this.maxZoom, Math.max(this.minZoom, Number(raw) || 1));
    this.syncCrop();
  }

  public onFrameWheel(event: WheelEvent): void {
    event.preventDefault();
    this.setZoom(this.zoom * (event.deltaY < 0 ? 1.08 : 1 / 1.08));
  }

  public resetFraming(): void {
    this.zoom = 1;
    this.frameX = 0.5;
    this.frameY = 0.5;
    this.syncCrop();
  }

  public startFrameDrag(event: PointerEvent, mode: 'move' | 'scale'): void {
    if (this.screen !== 'adjust') return;
    event.preventDefault();
    event.stopPropagation();

    const cell = (event.currentTarget as HTMLElement).closest('.studio-artcell') as HTMLElement | null;
    const rect = (cell ?? (event.currentTarget as HTMLElement)).getBoundingClientRect();
    const side = Math.min(rect.width, rect.height);

    const centreX = rect.left + rect.width / 2;
    const centreY = rect.top + rect.height / 2;

    this.drag = {
      mode,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startFrameX: this.frameX,
      startFrameY: this.frameY,
      startZoom: this.zoom,
      startDistance: Math.max(8, Math.hypot(event.clientX - centreX, event.clientY - centreY)),
      displayW: Math.max(1, side * this.containedFractionX),
      displayH: Math.max(1, side * this.containedFractionY),
    };

    (event.currentTarget as HTMLElement).setPointerCapture?.(event.pointerId);
  }

  public onFrameDrag(event: PointerEvent): void {
    const drag = this.drag;
    if (!drag || drag.pointerId !== event.pointerId) return;
    event.preventDefault();

    if (drag.mode === 'scale') {
      const cell = (event.currentTarget as HTMLElement).closest('.studio-artcell') as HTMLElement | null;
      const rect = (cell ?? (event.currentTarget as HTMLElement)).getBoundingClientRect();
      const distance = Math.hypot(
        event.clientX - (rect.left + rect.width / 2),
        event.clientY - (rect.top + rect.height / 2));

      this.setZoom(drag.startZoom * (distance / drag.startDistance));
      return;
    }

    const travelX = event.clientX - drag.startX;
    const travelY = event.clientY - drag.startY;

    if (this.usesFrame) {
      // Moving the WORK itself, so it follows the pointer: right is right. The canvas is drawn at
      // full size whatever the scale, so a pixel of travel is the same fraction of canvas at every
      // scale — which is exactly why the offset is measured against the canvas and not against the
      // leftover space. Measured against the leftover, a nudge threw the work across the frame at
      // 0.9× and did nothing at all at 1×.
      this.frameX = drag.startFrameX + travelX / drag.displayW;
      this.frameY = drag.startFrameY + travelY / drag.displayH;
    } else {
      // Dragging the picture right shows more of its LEFT side, so the kept region moves the other
      // way. The visible picture is magnified by the zoom, so a pixel of pointer travel is worth
      // less of the source the further in the artist is.
      this.frameX = drag.startFrameX - travelX / (drag.displayW * this.zoom);
      this.frameY = drag.startFrameY - travelY / (drag.displayH * this.zoom);
    }

    this.syncCrop();
  }

  public endFrameDrag(event: PointerEvent): void {
    if (this.drag?.pointerId === event.pointerId) {
      (event.currentTarget as HTMLElement).releasePointerCapture?.(event.pointerId);
      this.drag = null;
    }
  }

  /**
   * Derives `Geometry.Crop` from zoom and centre, and keeps the frame inside the photograph.
   *
   * Clamping here rather than at the edges of the drag is deliberate: the artist can keep dragging
   * and the frame simply stops, instead of the picture detaching from the cursor and drifting.
   */
  private syncCrop(): void {
    if (this.usesFrame) {
      // Placing the work on a canvas: it may be resized, moved, or both. `frameX/Y` stay 0..1 with
      // 0.5 as centred — the same numbers the crop path uses — and are converted to the ±0.5
      // offset the wire carries. Clamping here is what stops the work being pushed more than
      // halfway out of its own frame.
      this.frameX = Math.min(1, Math.max(0, this.frameX));
      this.frameY = Math.min(1, Math.max(0, this.frameY));

      const offsetX = this.frameX - 0.5;
      const offsetY = this.frameY - 0.5;
      const moved = Math.abs(offsetX) > 0.001 || Math.abs(offsetY) > 0.001;

      this.recipe.Geometry.Crop = null;
      this.recipe.Geometry.Frame = (this.zoom < 0.999 || moved)
        ? { Scale: this.zoom, X: offsetX, Y: offsetY }
        : null;
      return;
    }

    this.recipe.Geometry.Frame = null;

    const size = Math.min(1, 1 / this.zoom);
    const half = size / 2;

    // Clamped so the frame stops at the edge of the photograph rather than the picture detaching
    // from the cursor and drifting off on its own.
    this.frameX = Math.min(1 - half, Math.max(half, this.frameX));
    this.frameY = Math.min(1 - half, Math.max(half, this.frameY));

    this.recipe.Geometry.Crop = size >= 0.999
      ? null
      : { X: this.frameX - half, Y: this.frameY - half, Width: size, Height: size };
  }

  /**
   * The longest edge the WORK would be left with. Honest about what framing actually costs.
   *
   * Both directions cost resolution, for different reasons: zooming in keeps a smaller piece of the
   * original, and standing the work back resamples it smaller inside the same canvas. Either way
   * the artwork itself ends up described by fewer pixels than the camera captured.
   */
  public get croppedLongestEdge(): number {
    if (!this.naturalWidth || !this.naturalHeight) return 0;
    const longest = Math.max(this.naturalWidth, this.naturalHeight);
    return Math.round(longest * Math.min(this.zoom, 1 / this.zoom));
  }

  /**
   * True once the kept area is smaller than a catalogue image.
   *
   * Said out loud rather than silently absorbed, because the alternative is upscaling — inventing
   * pixels that were never photographed — which this pipeline will not do. The artist can still
   * go ahead; they simply get to know that the result will be smaller.
   */
  public get isBelowCatalogueStandard(): boolean {
    return this.croppedLongestEdge > 0 && this.croppedLongestEdge < this.catalogueEdge;
  }

  // ------------------------------------------------------------- adjust

  public sliderValue(def: SliderDef): number {
    if (def.key === 'Zoom') return this.zoom;
    if (def.key === 'Rotate') return this.recipe.Geometry.Rotate;
    if (def.key === 'SharpenAmount') return this.recipe.Adjustments.Sharpen.Amount;
    return this.recipe.Adjustments[def.key];
  }

  public setSlider(def: SliderDef, raw: string | number): void {
    const value = Number(raw);
    if (def.key === 'Zoom') { this.setZoom(value); return; }
    if (def.key === 'Rotate') { this.recipe.Geometry.Rotate = value; return; }
    if (def.key === 'SharpenAmount') { this.recipe.Adjustments.Sharpen.Amount = value; return; }
    this.recipe.Adjustments[def.key] = value;
  }

  /**
   * Suggested correction. Real analysis lands with the WebGL preview; these are sane defaults.
   *
   * Colour is deliberately left alone. Light, straightness and sharpness are photography — the
   * camera got them wrong and Auto is offering to put them right. How saturated the work is, is
   * the artist's decision about their own pigment, and guessing at it unasked is the kind of
   * "improvement" they would notice and object to.
   */
  public applyAuto(): void {
    this.recipe.Geometry.Rotate = 2.6;
    this.recipe.Adjustments.Brightness = 0.28;
    this.recipe.Adjustments.Contrast = 0.22;
    this.recipe.Adjustments.Temperature = -0.16;
    this.recipe.Adjustments.Sharpen.Amount = 0.42;
  }

  public resetAdjustments(): void {
    this.recipe = neutralRecipe(this.original?.Id || '');
    // Framing lives outside the recipe while it is being manipulated, so a reset that only
    // replaced the recipe would clear the crop and leave the handles where the artist left them.
    this.zoom = 1;
    this.frameX = 0.5;
    this.frameY = 0.5;
  }

  /** Submit the recipe. Free, so there's no confirmation and no quota check. */
  public applyAdjustments(): void {
    if (!this.hasAdjustments) { this.go('open'); return; }
    this.cancelInFlight();
    this.isBusy = true;
    this.go('working');
    this.studio.renderRecipe(this.recipe)
      .pipe(takeUntil(this.jobPoll$), takeUntil(this.destroy$))
      .subscribe({
        next: job => this.watchJob(job, 'versions'),
        error: () => this.fail('imageStudio.errorRenderFailed'),
      });
  }

  // ------------------------------------------------------------- generate

  /**
   * Open the product-shot screen.
   *
   * A product shot is the one outcome that cannot be produced from the photo alone — it needs a
   * setting, and asking for one afterwards would mean either guessing or charging the artist for
   * a room they never chose. So this outcome gets a step and the catalogue image does not.
   */
  public startScene(): void {
    if (this.blockedBy !== 'none') { this.errorKey = this.quotaMessageKey; return; }
    if (!this.canGenerate) { return; }
    this.go('scene');
  }

  public chooseSceneSetting(setting: SceneSetting): void {
    this.sceneSettingId = setting.id;
  }

  /** What the artist has typed, bounded — the counter beside the box reads from this. */
  public get sceneDetailUsed(): number {
    return (this.sceneDetail || '').trim().length;
  }

  public createScene(): void {
    this.generate('Scene', {
      promptId: this.sceneSettingId,
      sceneDescription: this.sceneDetail,
    });
  }

  public generate(operation: GenerateOperation, options: GenerateOptions = {}): void {
    if (this.blockedBy !== 'none') { this.errorKey = this.quotaMessageKey; return; }
    if (!this.canGenerate) { return; }
    const source = this.workingAsset || this.original;
    if (!source) { this.fail('imageStudio.errorNoImage'); return; }

    this.lastOperation = operation;
    // Held so "Try again" repeats the setting the artist chose rather than silently reverting to
    // the default — a second shot of a different room is not the retry they asked for.
    this.lastOptions = options;
    this.cancelInFlight();
    this.isBusy = true;
    this.go('working');

    this.studio.generate(operation, source.Id, options)
      .pipe(takeUntil(this.jobPoll$), takeUntil(this.destroy$))
      .subscribe({
        next: job => this.watchJob(job, 'review'),
        error: err => {
          if (err && err.status === 429) {
            // The server is the authority on the count — adopt its figures, then say
            // which window actually stopped us.
            if (err.error) this.quota = err.error;
            this.fail(this.quotaMessageKey || 'imageStudio.errorNoneLeftToday');
          } else if (err && err.code === 'IMAGE_SCENE_SETTING_UNKNOWN') {
            // The client and the server disagree about which settings exist — which happens when
            // one has shipped and the other has not. Say which decision to change instead of the
            // generic "that didn't work", and go back to the screen holding the choice.
            this.errorKey = 'imageStudio.errorSceneSettingUnknown';
            this.isBusy = false;
            this.go('scene', true);
          } else {
            this.fail('imageStudio.errorGenerateFailed');
          }
        },
      });
  }

  /**
   * A deliberate second go, which means a NEW idempotency key.
   *
   * Without this the server correctly honours the key it was given and returns the
   * ORIGINAL job — the one the artist just rejected — so "Try again" could never
   * produce a different image, and after a failure it returned the same failure for
   * ever. The key exists to make a *retry of one action* safe, not to freeze the
   * artist's next decision.
   */
  public tryAgain(): void {
    this.pendingAsset = null;
    const source = this.workingAsset || this.original;
    if (source) { this.studio.newAttempt(this.lastOperation, source.Id); }
    this.generate(this.lastOperation, this.lastOptions);
  }

  public keepResult(): void {
    if (!this.pendingAsset) return;
    this.isBusy = true;
    this.studio.acceptAsset(this.pendingAsset.Id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: asset => {
          this.isBusy = false;
          this.pendingAsset = null;
          this.refreshAssets();
          this.imageAccepted.emit(asset);
          this.go('versions');
        },
        error: () => this.fail('imageStudio.errorSaveFailed'),
      });
  }

  public discardResult(): void {
    if (!this.pendingAsset) { this.go('open'); return; }
    this.isBusy = true;
    this.studio.declineAsset(this.pendingAsset.Id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isBusy = false;
          this.pendingAsset = null;
          this.refreshAssets();
          this.go('open');
        },
        error: () => this.fail('imageStudio.errorSaveFailed'),
      });
  }

  /**
   * Cancelling before a job finishes must not cost the artist anything — and must not
   * surprise them by completing anyway. This tears down the whole in-flight chain,
   * including a submission that has not yet returned a JobId; cancelling the poll alone
   * left the request running, and the result still arrived on the review screen.
   */
  private cancelInFlight(): void {
    this.jobPoll$.next();
  }

  public cancelJob(): void {
    this.cancelInFlight();
    this.isBusy = false;
    this.go('open');
  }

  // ------------------------------------------------------------- job polling

  /**
   * Poll until the job settles. Deliberately polling rather than blocking: renders can
   * take double-digit seconds, and the artist is told they may leave the page.
   */
  private watchJob(job: RenderJob, doneScreen: StudioScreen): void {
    const startedAt = Date.now();

    timer(0, 900).pipe(
      switchMap(() => this.studio.getJob(job.JobId)),
      takeWhile(j => j.Status === 'Queued' || j.Status === 'Running', true),
      takeUntil(this.jobPoll$),
      takeUntil(this.destroy$),
    ).subscribe({
      next: j => {
        if (j.Status === 'Succeeded') { this.onJobDone(j, doneScreen); }
        else if (j.Status === 'Failed') {
          // Surface the server's own reason. A generic "that didn't work" hid
          // "Operation 'catalogue' is not implemented yet" — the one sentence that
          // told us where the gap actually was.
          this.errorDetail = j.Error || '';
          this.fail('imageStudio.errorGenerateFailed');
          this.releaseAttempt();
        }
        // The API's job worker runs in-process and IIS stops it when the site idles,
        // which leaves a job sitting in Queued that nothing will ever pick up. Polling
        // for ever renders as a progress bar that never moves and never explains
        // itself; say so instead, and stop asking.
        else if (Date.now() - startedAt > STUDIO_JOB_TIMEOUT_MS) {
          this.cancelInFlight();
          this.fail('imageStudio.errorTimedOut');
          this.releaseAttempt();
        }
      },
      error: () => this.fail('imageStudio.errorGenerateFailed'),
    });
  }

  /**
   * Frees the idempotency key after an attempt that produced nothing, so the artist's
   * next click is a real attempt rather than a replay of the failure.
   */
  private releaseAttempt(): void {
    const source = this.workingAsset || this.original;
    if (source) { this.studio.newAttempt(this.lastOperation, source.Id); }
  }

  private onJobDone(job: RenderJob, doneScreen: StudioScreen): void {
    this.isBusy = false;
    this.studio.getAssets(this.artistProductId, this.imageUrl)
      .pipe(takeUntil(this.destroy$))
      .subscribe(list => {
        this.assets = list;
        const result = list.find(a => a.Id === job.ResultAssetId) || null;
        if (doneScreen === 'review') {
          this.pendingAsset = result;
        } else if (result) {
          this.workingAsset = result;
          this.recipe = neutralRecipe(this.original?.Id || '');
        }
        if (result) { this.applyPendingLabel(result); }
        this.go(doneScreen);
      });
  }

  /**
   * The name the artist typed BEFORE the version existed, put onto it now that it does.
   *
   * Naming belongs here rather than only in the versions list, because the label answers "why did
   * I make this?" and the one moment the artist reliably knows the answer is while they are making
   * it — not later, choosing between four thumbnails of the same flower.
   *
   * A failure is deliberately quiet. The version rendered and is safe; losing a name is a smaller
   * harm than an error banner over a screen that otherwise succeeded, and the name can be typed
   * again on the version itself.
   */
  private applyPendingLabel(asset: ImageAsset): void {
    const name = (this.pendingLabel || '').trim();
    if (!name) { return; }

    this.pendingLabel = '';
    this.studio.setAssetLabel(asset.Id, name)
      .pipe(takeUntil(this.destroy$))
      .subscribe({ next: updated => { asset.Label = updated.Label; }, error: () => {} });
  }

  private refreshAssets(): void {
    this.studio.getAssets(this.artistProductId, this.imageUrl)
      .pipe(takeUntil(this.destroy$))
      .subscribe(list => {
        this.assets = list;
        this.workingAsset = this.preferredWorkingAsset(list);
      });
  }

  private fail(key: string): void {
    this.isBusy = false;
    this.errorKey = key;
    this.go('open', true);   // keep the message — see go()
  }

  // ------------------------------------------------------------- compare

  public setComparePercent(value: number): void {
    this.comparePercent = Math.max(0, Math.min(100, value));
  }

  public onCompareKey(event: KeyboardEvent): void {
    if (event.key === 'ArrowLeft') { this.setComparePercent(this.comparePercent - 4); event.preventDefault(); }
    if (event.key === 'ArrowRight') { this.setComparePercent(this.comparePercent + 4); event.preventDefault(); }
  }

  /**
   * Label for a version row — the artist's language, not the contract's.
   *
   * Which outcome produced an asset comes from `Operation`, which the API populates for
   * exactly this purpose. Inferring it from the prompt id worked only for as long as
   * prompt ids happened to start with the operation name.
   */
  public versionLabelKey(asset: ImageAsset): string {
    if (asset.Kind === 'Original') return 'imageStudio.versionOriginal';
    if (asset.Kind === 'Corrected') return 'imageStudio.versionCorrected';
    return asset.Operation === 'scene'
      ? 'imageStudio.versionScene' : 'imageStudio.versionCatalogue';
  }

  // ------------------------------------------------------- choosing and keeping

  /** Matches the server column width; a longer note is trimmed there anyway. */
  public readonly LABEL_MAX = 200;

  /** Which row is being renamed, if any. */
  public editingLabelFor: string | null = null;
  public labelDraft = '';

  /** A name typed on Adjust, before the version it belongs to has been rendered. */
  public pendingLabel = '';

  /**
   * The version the artist is looking at on the Versions screen.
   *
   * That screen exists to choose between versions, and until now it showed one fixed image while
   * the artist picked from thumbnails the size of a stamp — choosing blind. Null means "whatever
   * the artwork is using", which is the right thing to show before anything is selected.
   */
  public selectedVersionId: string | null = null;

  public get selectedVersion(): ImageAsset | null {
    if (!this.selectedVersionId) return null;
    return this.assets.find(a => a.Id === this.selectedVersionId) || null;
  }

  public selectVersion(asset: ImageAsset): void {
    this.selectedVersionId = asset?.Id || null;
  }

  /**
   * Only the artist's own adjustments can be removed.
   *
   * A generation cost real money and cannot be re-derived from a recipe, so deleting one
   * destroys something that was paid for — a different decision, deliberately not offered
   * beside a free adjustment. The current image is also protected: an artwork pointing at
   * nothing is worse than an unwanted version. The server enforces both regardless.
   */
  public canDelete(asset: ImageAsset): boolean {
    return asset.Kind === 'Corrected' && !asset.IsCurrent;
  }

  /**
   * A product shot is a MARKETING image and can never be the artwork's main picture.
   *
   * Its frame contains a room, a shelf, a pair of hands — so promoting it would mean a buyer opening
   * the artwork sees the setting rather than the work. It still joins the artwork's pictures on
   * accept, which is where a lifestyle shot belongs and how the artist actually uses it.
   *
   * The server refuses this too (`IMAGE_SCENE_IS_MARKETING`). This is what stops us offering a
   * button that could only ever fail.
   */
  public canSetMain(asset: ImageAsset): boolean {
    return !asset.IsCurrent && !isMarketingAsset(asset);
  }

  public isMarketing(asset: ImageAsset): boolean {
    return isMarketingAsset(asset);
  }

  /**
   * "Use THIS one." The explicit choice the screen never had.
   *
   * Until now the artwork's image was inferred from the lineage, and when the inference said
   * no the artist had no way to disagree — an accepted catalogue image simply never arrived.
   */
  public setMain(asset: ImageAsset): void {
    if (this.isBusy || asset.IsCurrent) { return; }
    this.isBusy = true;

    this.studio.setMainAsset(asset.Id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: updated => {
          this.isBusy = false;
          // Reflect it immediately rather than waiting for a refetch: the badge moving is
          // the confirmation that the click did anything.
          for (const version of this.assets) { version.IsCurrent = version.Id === updated.Id; }
          this.mainChanged = true;
          this.imageAccepted.emit(updated);
        },
        error: () => { this.isBusy = false; this.fail('imageStudio.errorSetMainFailed'); },
      });
  }

  public deleteVersion(asset: ImageAsset): void {
    if (this.isBusy || !this.canDelete(asset)) { return; }
    this.isBusy = true;

    this.studio.deleteAsset(asset.Id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isBusy = false;
          this.assets = this.assets.filter(a => a.Id !== asset.Id && a.SourceAssetId !== asset.Id);
          if (this.workingAsset?.Id === asset.Id) { this.workingAsset = this.preferredWorkingAsset(this.lineage); }
        },
        error: () => { this.isBusy = false; this.fail('imageStudio.errorDeleteFailed'); },
      });
  }

  public editLabel(asset: ImageAsset): void {
    this.editingLabelFor = asset.Id;
    this.labelDraft = asset.Label || '';
  }

  public cancelLabel(): void {
    this.editingLabelFor = null;
    this.labelDraft = '';
  }

  /**
   * Saves the note, and only when it actually changed — this fires on blur as well as Enter,
   * so without the comparison every glance at the field would be a write.
   */
  public saveLabel(asset: ImageAsset): void {
    if (this.editingLabelFor !== asset.Id) { return; }

    const next = (this.labelDraft || '').trim();
    this.editingLabelFor = null;

    if (next === (asset.Label || '')) { return; }

    this.studio.setAssetLabel(asset.Id, next)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: updated => { asset.Label = updated.Label; },
        error: () => this.fail('imageStudio.errorLabelFailed'),
      });
  }

  /** Set once the artist changes the main image, so Done knows whether anything moved. */
  private mainChanged = false;

  /**
   * "Yes, this is right — take me back."
   *
   * Deliberately NOT a save: every version is already stored the moment it is made, which is
   * what "nothing is ever overwritten" means. The only decision this screen holds is which
   * version the artwork uses, and Set to Main has already committed that. Done simply closes,
   * telling the page to refresh so the new image is on the form the artist returns to.
   */
  public finish(): void {
    this.jobPoll$.next();
    this.close();
  }

  /**
   * Leaves without changing the artwork.
   *
   * It cannot "undo a save" — the versions exist and a generation cost money, so discarding
   * them would destroy what the artist paid for. What it abandons is the intent to change the
   * artwork's image; the work stays in Versions to come back to.
   */
  public cancelStudio(): void {
    this.jobPoll$.next();
    this.close();
  }

  public trackByAssetId(_index: number, asset: ImageAsset): string {
    return asset.Id;
  }

  /**
   * Hand off to the existing editor for a tool the studio doesn't implement itself.
   *
   * Reuse rather than reimplement: the corner-pick + aspect-ratio flow already works
   * and is deterministic. Note the result is saved through the OLD path, so it will
   * not appear in this studio's version list until perspective moves into the recipe
   * and the server renders it from the original.
   */
  public useTool(tool: 'perspective'): void {
    // The anchor travels with the hand-off. The studio is closed while the artist works
    // in the editor, so by the time a rectification comes back there is nothing left to
    // ask which photo it belonged to — and the server needs the source asset to render
    // the recipe against.
    this.openTool.emit({ tool, sourceAssetId: this.rootAssetId });
  }
}
